import { assertNever } from "@msys/common";
import { LabeledValue, Modal, ModalOpenButton } from "@msys/ui";
import { Unit } from "@msys/units";
import { Edit as EditIcon } from "@mui/icons-material";
import { Alert, Box, IconButton, Stack, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { capitalize, merge, noop, omit, uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import {
  DefineItemProps2Entry,
  DefineItemProps2EntryBool,
  DefineItemProps2EntryBoolComputed,
  DefineItemProps2EntryNumber,
  DefineItemProps2EntryNumberArray,
  DefineItemProps2EntryNumberArrayComputed,
  DefineItemProps2EntryNumberComputed,
  DefineItemProps2EntryText,
  DefineItemProps2EntryTextArray,
  DefineItemProps2EntryTextArrayComputed,
  DefineItemProps2EntryTextComputed,
  DocType,
  PermissionName,
  Props2,
} from "../../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug } from "../../../auth/RestrictedByCapabilityAndDocumentPermissionByType.js";
import { type DocumentByType } from "../../../auth/RestrictedByDocumentPermissionByType.js";
import { AutocompleteField } from "../../../commons/form-fields/AutocompleteField.js";
import { CheckboxField } from "../../../commons/form-fields/CheckboxField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { AskIfConditionExpressionField } from "../fields/AskIfConditionExpressionField.js";
import {
  AllowedValuesField,
  AllowedValuesFormValues,
  useAllowedValuesFieldValidationSchema,
} from "../form-fields/PropertyAllowedValuesField.js";
import {
  getValuesMediaBool,
  useValuesMediaBoolFieldValidationSchema,
  ValuesMediaBoolField,
  ValuesMediaBoolFormValues,
} from "../form-fields/PropertyValuesMediaBoolField.js";
import {
  NumberRangeSection,
  NumberRangeSectionFormValues,
  useNumberRangeValidationSchema,
} from "../NumberRangeSection.js";
import {
  getAllowedValues,
  getPropertyType,
  getPropValues,
  isArrayType,
  isBooleanType,
  isNonComputedProp,
  isNumberType,
  isTextType,
  PROP_VALUES,
  propToDefineInput,
  TypeFields,
} from "../properties.js";
import { PropertyField } from "../PropertyField.js";
import {
  QuestionControlSection,
  QuestionControlSectionFormValues,
  useQuestionControlValidationSchema,
} from "../QuestionControlSection.js";
import { usePropertyUnits } from "../usePropertyUnits.js";
import { PropertyKeyEditModal } from "./PropertyKeyEditModal.js";

export interface FormValues
  extends
    TypeFields,
    AllowedValuesFormValues,
    ValuesMediaBoolFormValues,
    QuestionControlSectionFormValues,
    NumberRangeSectionFormValues {
  label: string;
  unit: Unit | null;
  essential: boolean;
  group: string;
  clientVisibility: boolean;
  askIfExpr: string;
  customizable: boolean;
}

interface Props {
  docType: DocType;
  projectId: string | null;
  docId: string;
  itemId: string;
  property: Props2;
  handleClose: () => void;
  handleComplete: (changedProperty: DefineItemProps2Entry) => Promise<void>;
  documentViewerPermissions: PermissionName[];
}

export function PropertyEditModal({
  docType,
  projectId,
  docId,
  itemId,
  property,
  handleClose,
  handleComplete,
  documentViewerPermissions,
}: Props) {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const { getUnits } = usePropertyUnits();
  const unitsOptions = React.useMemo(() => getUnits(), [getUnits]);
  const [error, setError] = React.useState<Error | null>(null);

  const document: DocumentByType = {
    docType,
    viewerPermissions: documentViewerPermissions,
  };

  const initialValues = React.useMemo(
    () => getInitialValues(property, unitsOptions),
    [property, unitsOptions]
  );

  const propValues = React.useMemo(() => getPropValues(property), [property]);

  const allowedValuesSchema = useAllowedValuesFieldValidationSchema(propValues);
  const valuesMediaBoolSchema = useValuesMediaBoolFieldValidationSchema();
  const questionControlSchema = useQuestionControlValidationSchema();
  const numberRangeSchema = useNumberRangeValidationSchema(propValues);

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        label: Yup.string()
          .required()
          .min(1)
          .label(t("Name", { ns: "Global" })),
        type: Yup.string().oneOf(PROP_VALUES).required(),
        essential: Yup.boolean().required(),
        customizable: Yup.boolean().required(),
        unit: Yup.object().shape({ key: Yup.string().required() }).nullable(),
        group: Yup.string(),
        ...numberRangeSchema,
        ...allowedValuesSchema,
        ...valuesMediaBoolSchema,
        ...questionControlSchema,
        askIfExpr: Yup.string(),
      }),
    [
      t,
      numberRangeSchema,
      allowedValuesSchema,
      valuesMediaBoolSchema,
      questionControlSchema,
    ]
  );

  const onSubmit = async (values: FormValues) => {
    try {
      await handleComplete(createPropertyEntry(values, property));
      handleClose();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(JSON.stringify(err));
      setError(error);
    }
  };

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={onSubmit}
    >
      {formikProps => (
        <Modal
          title={t("Edit property", {
            ns: "QuoteItem",
          })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Confirm", {
                ns: "Global",
              }),
              buttonProps: {
                form: formId,
                type: "submit",
                loading: formikProps.isSubmitting,
                disabled: !formikProps.isValid || !formikProps.dirty,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack spacing={1}>
              {error && <Alert severity="error">{error.message}</Alert>}
              <Stack direction={"row"} alignSelf="flex-start" spacing={2}>
                <LabeledValue label={t("Key", { ns: "QuoteItem" })}>
                  {property.key}
                </LabeledValue>
                <ModalOpenButton
                  Modal={PropertyKeyEditModal}
                  modalProps={{ projectId, docId, itemId, property }}
                  disabled={formikProps.dirty}
                >
                  <Tooltip
                    title={
                      formikProps.dirty
                        ? t("Can't change key with unsaved changes", {
                            ns: "QuoteItem",
                          })
                        : ""
                    }
                  >
                    <Box display="flex">
                      <IconButton
                        color={"primary"}
                        size="small"
                        disabled={formikProps.dirty}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </Tooltip>
                </ModalOpenButton>
              </Stack>

              <TextField
                name={"label"}
                label={t("Label", { ns: "Global" })}
                autoFocus
              />
              <LabeledValue label={t("Type", { ns: "QuoteItem" })}>
                {capitalize(formikProps.values.type)}
              </LabeledValue>
              <PropertyField
                label={t("Current value", {
                  ns: "Global",
                })}
                projectId={projectId}
                docId={docId}
                itemId={itemId}
                property={property}
                setPropertyValue={noop}
                readOnly
              />
              {formikProps.values.type === "NUMBER" && (
                <AutocompleteField
                  name="unit"
                  inputLabel={t("Unit", {
                    ns: "QuoteItem",
                  })}
                  options={unitsOptions}
                  getOptionLabel={option => option.value}
                />
              )}
              {formikProps.values.type !== "BOOLEAN" && (
                <CheckboxField
                  name={"isArray"}
                  label={t("Allow multiple values", {
                    ns: "QuoteItem",
                  })}
                  disabled
                />
              )}
              {formikProps.values.type !== "BOOLEAN" &&
                isNonComputedProp(property) && (
                  <AllowedValuesField
                    type={formikProps.values.type}
                    disabled={
                      (formikProps.values.type === "NUMBER" &&
                        (formikProps.values.setRange ||
                          formikProps.values.setStep)) ||
                      formikProps.isSubmitting
                    }
                  />
                )}
              {formikProps.values.type === "BOOLEAN" &&
                isNonComputedProp(property) && (
                  <ValuesMediaBoolField disabled={formikProps.isSubmitting} />
                )}
              {formikProps.values.type === "NUMBER" &&
                isNonComputedProp(property) && (
                  <NumberRangeSection
                    disabled={formikProps.values.defineAllowedValues}
                  />
                )}
              <TextField name="group" label={t("Group", { ns: "QuoteItem" })} />
              <CheckboxField
                name="clientVisibility"
                label={t("Visibility", { ns: "Global" })}
              />
              {docType === "TEMPLATE" && (
                <RestrictedByCapabilityWithDebug capability="TEMPLATE_SHARING">
                  <CheckboxField
                    name="customizable"
                    label={t("Customisable", { ns: "QuoteItem" })}
                  />
                </RestrictedByCapabilityWithDebug>
              )}
              {isNonComputedProp(property) && (
                <QuestionControlSection isInitiallyExpanded />
              )}
              {isNonComputedProp(property) && (
                <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                  capability="EXPRESSIONS"
                  permissionByType={{
                    QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                    REQUIREMENT: "MANAGE_REQUIREMENTS_EXPRESSIONS",
                    TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                    BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                  }}
                  document={document}
                  otherwise={
                    <AskIfConditionExpressionField
                      projectId={projectId}
                      itemId={itemId}
                      docId={docId}
                      name={"askIfExpr"}
                      label={t("Ask if condition", { ns: "QuoteItem" })}
                      addButtonLabel={t("Add condition", { ns: "QuoteItem" })}
                      currentItemId={itemId}
                      currentPropertyKey={property.key}
                      disabled={true}
                      readOnly={true}
                    />
                  }
                >
                  <AskIfConditionExpressionField
                    projectId={projectId}
                    itemId={itemId}
                    docId={docId}
                    name={"askIfExpr"}
                    label={t("Ask if condition", { ns: "QuoteItem" })}
                    addButtonLabel={t("Add condition", { ns: "QuoteItem" })}
                    currentItemId={itemId}
                    currentPropertyKey={property.key}
                    disabled={formikProps.isSubmitting}
                  />
                </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
              )}
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
}

export function createPropertyEntry(
  values: FormValues,
  property: Props2
): DefineItemProps2Entry {
  switch (property.__typename) {
    case "Props2Bool": {
      return {
        bool: {
          ...propToDefineInput(property).bool,
          ...createBooleanPropertyEntry(values),
        },
      };
    }
    case "Props2BoolComputed": {
      return {
        boolComputed: {
          ...propToDefineInput(property).boolComputed,
          ...createBooleanComputedPropertyEntry(values),
        },
      };
    }
    case "Props2Number": {
      return {
        number: {
          ...propToDefineInput(property).number,
          ...createNumberPropertyEntry(values),
        },
      };
    }
    case "Props2NumberComputed": {
      return {
        numberComputed: {
          ...propToDefineInput(property).numberComputed,
          ...createNumberComputedPropertyEntry(values),
        },
      };
    }
    case "Props2NumberArray": {
      return {
        numberArray: {
          ...propToDefineInput(property).numberArray,
          ...createNumberArrayPropertyEntry(values),
        },
      };
    }
    case "Props2NumberArrayComputed": {
      return {
        numberArrayComputed: {
          ...propToDefineInput(property).numberArrayComputed,
          ...createNumberArrayComputedPropertyEntry(values),
        },
      };
    }
    case "Props2Text": {
      return {
        text: {
          ...propToDefineInput(property).text,
          ...createTextPropertyEntry(values),
        },
      };
    }
    case "Props2TextComputed": {
      return {
        textComputed: {
          ...propToDefineInput(property).textComputed,
          ...createTextComputedPropertyEntry(values),
        },
      };
    }
    case "Props2TextArray": {
      return {
        textArray: {
          ...propToDefineInput(property).textArray,
          ...createTextArrayPropertyEntry(values),
        },
      };
    }
    case "Props2TextArrayComputed": {
      return {
        textArrayComputed: {
          ...propToDefineInput(property).textArrayComputed,
          ...createTextArrayComputedPropertyEntry(values),
        },
      };
    }
    default: {
      assertNever(property);
    }
  }
}

type EntryInputValues = FormValues;

function createBooleanPropertyEntry(
  values: EntryInputValues
): Omit<DefineItemProps2EntryBool, "key" | "valueBool"> {
  return {
    label: values.label,
    group: values.group,
    essential: values.essential,
    valuesMediaBool: values.defineValuesMediaBool
      ? values.valuesMediaBool
          .filter(v => v.media)
          .map(v => ({
            bool: v.value,
            media: omit(v.media!, "id", "__typename", "__type"),
          }))
      : [],
    clientVisibility: values.clientVisibility,
    prompt: values.prompt,
    questionControl: values.questionControl,
    // questionControl: [
    //   {
    //     askWhen: values.askWhen,
    //     askWhom: values.askWhom,
    //   },
    // ],
    askIfExpr: values.askIfExpr,
    customizable: values.customizable,
  };
}
function createBooleanComputedPropertyEntry(
  values: EntryInputValues
): Omit<DefineItemProps2EntryBoolComputed, "key" | "expr"> {
  return {
    label: values.label,
    clientVisibility: values.clientVisibility,
    group: values.group,
  };
}
function createNumberPropertyEntry(
  values: EntryInputValues
): Omit<DefineItemProps2EntryNumber, "key" | "valueNumber"> {
  return {
    label: values.label,
    group: values.group,
    essential: values.essential,
    allowedValuesNumber: values.defineAllowedValues
      ? values.allowedValues.map(v => ({
          allowedNumber: v.value as number,
          media: v.media ? omit(v.media, "id", "__typename", "__type") : null,
        }))
      : [],
    range: values.setRange ? values.range : null,
    step: values.setStep ? values.step : null,
    unit: values.unit?.key,
    clientVisibility: values.clientVisibility,
    prompt: values.prompt,
    questionControl: values.questionControl,
    // questionControl: [
    //   {
    //     askWhen: values.askWhen,
    //     askWhom: values.askWhom,
    //   },
    // ],
    askIfExpr: values.askIfExpr,
    customizable: values.customizable,
  };
}
function createNumberComputedPropertyEntry(
  values: EntryInputValues
): Omit<DefineItemProps2EntryNumberComputed, "key" | "expr"> {
  return {
    label: values.label,
    clientVisibility: values.clientVisibility,
    group: values.group,
    unit: values.unit?.key,
  };
}
function createNumberArrayPropertyEntry(
  values: EntryInputValues
): Omit<DefineItemProps2EntryNumberArray, "key" | "valueNumberArray"> {
  return {
    label: values.label,
    group: values.group,
    essential: values.essential,
    allowedValuesNumber: values.defineAllowedValues
      ? values.allowedValues.map(v => ({
          allowedNumber: v.value as number,
          media: v.media ? omit(v.media, "id", "__typename", "__type") : null,
        }))
      : [],
    range: values.setRange ? values.range : null,
    step: values.setStep ? values.step : null,
    unit: values.unit?.key,
    clientVisibility: values.clientVisibility,
    prompt: values.prompt,
    questionControl: values.questionControl,
    // questionControl: [
    //   {
    //     askWhen: values.askWhen,
    //     askWhom: values.askWhom,
    //   },
    // ],
    askIfExpr: values.askIfExpr,
    customizable: values.customizable,
  };
}
function createNumberArrayComputedPropertyEntry(
  values: EntryInputValues
): Omit<DefineItemProps2EntryNumberArrayComputed, "key" | "expr"> {
  return {
    label: values.label,
    clientVisibility: values.clientVisibility,
    group: values.group,
    unit: values.unit?.key,
  };
}
function createTextPropertyEntry(
  values: EntryInputValues
): Omit<DefineItemProps2EntryText, "key" | "valueText"> {
  return {
    label: values.label,
    group: values.group,
    essential: values.essential,
    allowedValuesText: values.defineAllowedValues
      ? values.allowedValues.map(v => ({
          allowedText: v.value as string,
          media: v.media ? omit(v.media, "id", "__typename", "__type") : null,
        }))
      : [],
    clientVisibility: values.clientVisibility,
    prompt: values.prompt,
    questionControl: values.questionControl,
    // questionControl: [
    //   {
    //     askWhen: values.askWhen,
    //     askWhom: values.askWhom,
    //   },
    // ],
    askIfExpr: values.askIfExpr,
    customizable: values.customizable,
  };
}
function createTextComputedPropertyEntry(
  values: EntryInputValues
): Omit<DefineItemProps2EntryTextComputed, "key" | "expr"> {
  return {
    label: values.label,
    clientVisibility: values.clientVisibility,
    group: values.group,
  };
}
function createTextArrayPropertyEntry(
  values: EntryInputValues
): Omit<DefineItemProps2EntryTextArray, "key" | "valueTextArray"> {
  return {
    label: values.label,
    group: values.group,
    essential: values.essential,
    allowedValuesText: values.defineAllowedValues
      ? values.allowedValues.map(v => ({
          allowedText: v.value as string,
          media: v.media ? omit(v.media, "id", "__typename", "__type") : null,
        }))
      : [],
    clientVisibility: values.clientVisibility,
    prompt: values.prompt,
    questionControl: values.questionControl,
    // questionControl: [
    //   {
    //     askWhen: values.askWhen,
    //     askWhom: values.askWhom,
    //   },
    // ],
    askIfExpr: values.askIfExpr,
    customizable: values.customizable,
  };
}
function createTextArrayComputedPropertyEntry(
  values: EntryInputValues
): Omit<DefineItemProps2EntryTextArrayComputed, "key" | "expr"> {
  return {
    label: values.label,
    clientVisibility: values.clientVisibility,
    group: values.group,
  };
}

export function getInitialValues(
  property: Props2,
  unitsOptions: Unit[]
): FormValues {
  return {
    label: property.label,
    type: getPropertyType(property),
    defineAllowedValues:
      isNonComputedProp(property) &&
      ((isTextType(property) && property.allowedValuesText.length > 0) ||
        (isNumberType(property) && property.allowedValuesNumber.length > 0)),
    allowedValues: getAllowedValues(property),
    defineValuesMediaBool:
      isNonComputedProp(property) &&
      isBooleanType(property) &&
      property.valuesMediaBool.length > 0,
    valuesMediaBool: getValuesMediaBool(property),
    setRange:
      isNumberType(property) &&
      isNonComputedProp(property) &&
      Boolean(property.range),
    setStep:
      isNumberType(property) &&
      isNonComputedProp(property) &&
      Boolean(property.step),
    range:
      isNumberType(property) && isNonComputedProp(property)
        ? merge(
            { min: null, max: null },
            {
              min: property.range?.min ?? null,
              max: property.range?.max ?? null,
            }
          )
        : { min: null, max: null },
    step:
      isNumberType(property) && isNonComputedProp(property)
        ? (property.step ?? null)
        : null,
    essential: false,
    unit:
      isNumberType(property) && property.unit
        ? (unitsOptions.find(option => option.key === property.unit) ?? null)
        : null,
    group: property.group,
    clientVisibility: property.clientVisibility,
    prompt: isNonComputedProp(property) ? property.prompt : "",
    questionControl: isNonComputedProp(property)
      ? property.questionControl
      : [],
    isArray: isArrayType(property),
    askIfExpr: isNonComputedProp(property) ? property.askIfExpr : "",
    customizable: isNonComputedProp(property) ? property.customizable : false,
  };
}

export function isRangeDefined(values: FormValues) {
  return (
    (values.range?.min ?? null) !== null || (values.range?.max ?? null) !== null
  );
}
